// StorboltProfileComponent.tsx

import React, { useEffect, useState } from "react";
import { useForm, FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "components/ui/select";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import PageHeader from "components/page-header";
import { useUserData } from "store/user-data";
import {
  BusinessDetails,
  getBusinessDetailsAction,
  setBusinessBankAccountAction,
} from "action/misc-action";
import Loading from "components/loading";
import { changePasswordAction } from "action/auth-action";
import { useSessionControl } from "hooks/use-session-control";

// Zod Schemas
const changePasswordSchema = z
  .object({
    currentPassword: z
      .string()
      .min(6, "Current password must be at least 6 characters"),
    newPassword: z
      .string()
      .min(6, "New password must be at least 6 characters"),
    confirmPassword: z
      .string()
      .min(6, "Confirm password must be at least 6 characters"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "New passwords do not match",
    path: ["confirmPassword"],
  });

const addAccountSchema = z.object({
  accountName: z.string().min(1, "Account name is required"),
  accountNumber: z.string().min(10, "Account number is required"),
  bankName: z.string().min(1, "Bank name is required"),
  bankCode: z.string().min(1, "Bank is required"),
});

// Nigerian Banks List
const banks = [
  { name: "Access Bank", code: "044" },
  { name: "Citibank Nigeria", code: "023" },
  { name: "Coronation Merchant Bank", code: "559" },
  { name: "Ecobank Nigeria", code: "050" },
  { name: "FBNQuest Merchant Bank", code: "560" },
  { name: "First Bank of Nigeria", code: "011" },
  { name: "First City Monument Bank (FCMB)", code: "214" },
  { name: "Globus Bank", code: "00103" },
  { name: "Guaranty Trust Bank", code: "058" },
  { name: "Heritage Bank", code: "030" },
  { name: "Jaiz Bank", code: "301" },
  { name: "Keystone Bank", code: "082" },
  { name: "Parallex Bank", code: "526" },
  { name: "Polaris Bank", code: "076" },
  { name: "Providus Bank", code: "101" },
  { name: "Rand Merchant Bank", code: "502" },
  { name: "Stanbic IBTC Bank", code: "221" },
  { name: "Standard Chartered Bank", code: "068" },
  { name: "Sterling Bank", code: "232" },
  { name: "SunTrust Bank", code: "100" },
  { name: "Titan Trust Bank", code: "102" },
  { name: "Union Bank of Nigeria", code: "032" },
  { name: "United Bank for Africa (UBA)", code: "033" },
  { name: "Unity Bank", code: "215" },
  { name: "Wema Bank", code: "035" },
  { name: "Zenith Bank", code: "057" },
  { name: "Fidelity Bank", code: "070" },
  { name: "Abbey Mortgage Bank", code: "801" },
  { name: "Living Trust Mortgage Bank", code: "031" },
  { name: "Gateway Mortgage Bank", code: "070" },
  { name: "FSDH Merchant Bank", code: "501" },
  { name: "Nova Merchant Bank", code: "561" },
  { name: "TAJBank", code: "302" },
  { name: "Guaranty Trust Holding Company", code: "105" },
  { name: "FCMB Merchant Bank", code: "50111" },
  { name: "OPay", code: "100004" },
  { name: "PalmPay", code: "999991" },
  { name: "Kuda Bank", code: "50211" },
  { name: "Paga", code: "100002" },
  { name: "Carbon", code: "100031" },
  { name: "FairMoney", code: "100003" },
  { name: "VFD Microfinance Bank", code: "566" },
  { name: "Rubies Bank", code: "125" },
  { name: "Eyowo", code: "100006" },
  { name: "Moniepoint", code: "50515" },
];

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;
type AddAccountFormData = z.infer<typeof addAccountSchema>;

/** One label/value line in a details section; stacks on phones. */
const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="grid gap-1 py-3.5 text-sm sm:grid-cols-[160px_1fr] sm:gap-4">
    <dt className="text-default-600">{label}</dt>
    <dd className="min-w-0 break-words font-medium text-default-900">{value}</dd>
  </div>
);

const StorboltProfileComponent = () => {
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
    useState(false);
  const [isAddAccountDialogOpen, setIsAddAccountDialogOpen] = useState(false);
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails>(null);
  const [isLoading, setIsLoading] = useState(true);
  const userData = useUserData.getState().userData;
  const { sessionControl } = useSessionControl();

  useEffect(() => {
    getBusinessDetailsAction().then((response) => {
      if (response.status === 200) {
        setBusinessDetails(response.body);
        setIsLoading(false);
      } else {
        sessionControl(response.status);
        toast.error(response.error);
        setIsLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Change Password Form
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    mode: "all",
  });

  const onSubmitChangePassword = (data: ChangePasswordFormData) => {
    // Handle change password logic
    const toastId = toast.loading("Changing password...");
    changePasswordAction(data).then((response) => {
      if (response.status === 200) {
        setIsChangePasswordDialogOpen(false);
        resetPasswordForm();
        toast.success("Password changed successfully.", { id: toastId });
      } else {
        setIsChangePasswordDialogOpen(false);
        resetPasswordForm();
        toast.error(response.error, { id: toastId });
      }
    });
  };

  // Add Account Form
  const {
    register: registerAccount,
    handleSubmit: handleAccountSubmit,
    reset: resetAccountForm,
    formState: { errors: accountErrors },
    setValue: setAccountValue,
  } = useForm<AddAccountFormData>({
    resolver: zodResolver(addAccountSchema),
    mode: "all",
  });

  const onSubmitAddAccount = (data: AddAccountFormData) => {
    // Handle add account logic
    const toastId = toast.loading("Adding details...");
    setBusinessBankAccountAction(data).then((response) => {
      if (response.status === 201) {
        setIsAddAccountDialogOpen(false);
        resetAccountForm();
        toast.success("Account added successfully.", { id: toastId });
      } else {
        if (response.status === 401) {
          toast.dismiss(toastId);
          setIsAddAccountDialogOpen(false);
          resetAccountForm();
          sessionControl(response.status);
          return;
        }
        setIsAddAccountDialogOpen(false);
        resetAccountForm();
        toast.error(response.error, { id: toastId });
      }
    });
  };

  // Helper function to get error messages
  const getErrorMessage = (
    errors: FieldErrors,
    fieldName: string,
  ): string | undefined => {
    const error = errors[fieldName];
    if (error) {
      if ("message" in error && typeof error.message === "string") {
        return error.message;
      }
    }
    return undefined;
  };

  return isLoading ? (
    <div className="h-screen">
      <Loading />
    </div>
  ) : (
    <div className="w-full">
      <PageHeader title="Profile" />

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Business Details */}
        <Card>
          <CardHeader className="border-b px-6 py-5">
            <CardTitle className="text-lg">Business Details</CardTitle>
          </CardHeader>
          <CardContent className="px-6 py-2">
            <dl className="divide-y">
              <DetailRow label="Business Name" value={businessDetails.name || "N/A"} />
              <DetailRow label="Address" value={businessDetails.address || "N/A"} />
              <DetailRow label="Email" value={businessDetails.email || "N/A"} />
              <DetailRow label="Phone" value={businessDetails.phone_number || "N/A"} />
            </dl>
          </CardContent>
        </Card>

        {/* Account Details */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 border-b px-6 py-5">
            <CardTitle className="text-lg">Account Details</CardTitle>
            {!businessDetails.bank_account.account_number && (
              <Button
                variant="outline"
                size="md"
                onClick={() => setIsAddAccountDialogOpen(true)}
              >
                Add Account
              </Button>
            )}
          </CardHeader>
          <CardContent className="px-6 py-2">
            <dl className="divide-y">
              <DetailRow label="Account Name" value={businessDetails.bank_account.name || "N/A"} />
              <DetailRow label="Bank" value={businessDetails.bank_account.bank_name || "N/A"} />
              <DetailRow label="Account Number" value={businessDetails.bank_account.account_number || "N/A"} />
            </dl>
          </CardContent>
        </Card>

        {/* User Info with Change Password */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 border-b px-6 py-5">
            <CardTitle className="text-lg">User Information</CardTitle>
            <Button
              variant="outline"
              size="md"
              onClick={() => setIsChangePasswordDialogOpen(true)}
            >
              Change Password
            </Button>
          </CardHeader>
          <CardContent className="px-6 py-2">
            <dl className="divide-y">
              <DetailRow label="Username" value={userData?.username} />
              <DetailRow label="Email" value={userData?.email ?? userData?.phone_number} />
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Change Password Dialog */}
      <Dialog
        open={isChangePasswordDialogOpen}
        onOpenChange={setIsChangePasswordDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and a new password.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={handlePasswordSubmit(onSubmitChangePassword)}
            className="space-y-4"
          >
            <div>
              <label
                htmlFor="currentPassword"
                className="block text-sm font-medium text-default-700"
              >
                Current Password
              </label>
              <Input
                id="currentPassword"
                type="text"
                {...registerPassword("currentPassword")}
                required
              />
              {getErrorMessage(passwordErrors, "currentPassword") && (
                <p className="text-destructive text-sm">
                  {getErrorMessage(passwordErrors, "currentPassword")}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-default-700"
              >
                New Password
              </label>
              <Input
                id="newPassword"
                type="text"
                {...registerPassword("newPassword")}
                required
              />
              {getErrorMessage(passwordErrors, "newPassword") && (
                <p className="text-destructive text-sm">
                  {getErrorMessage(passwordErrors, "newPassword")}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-default-700"
              >
                Confirm New Password
              </label>
              <Input
                id="confirmPassword"
                type="password"
                {...registerPassword("confirmPassword")}
                required
              />
              {getErrorMessage(passwordErrors, "confirmPassword") && (
                <p className="text-destructive text-sm">
                  {getErrorMessage(passwordErrors, "confirmPassword")}
                </p>
              )}
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsChangePasswordDialogOpen(false)}
                type="button"
              >
                Cancel
              </Button>
              <Button variant="default" type="submit">
                Change Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Account Dialog */}
      <Dialog
        open={isAddAccountDialogOpen}
        onOpenChange={setIsAddAccountDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Bank Account</DialogTitle>
            <DialogDescription>
              Enter your bank account details.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={handleAccountSubmit(onSubmitAddAccount)}
            className="space-y-4"
          >
            <div>
              <label
                htmlFor="accountName"
                className="block text-sm font-medium text-default-700"
              >
                Account Name
              </label>
              <Input
                id="accountName"
                {...registerAccount("accountName")}
                required
              />
              {getErrorMessage(accountErrors, "accountName") && (
                <p className="text-destructive text-sm">
                  {getErrorMessage(accountErrors, "accountName")}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="accountNumber"
                className="block text-sm font-medium text-default-700"
              >
                Account Number
              </label>
              <Input
                id="accountNumber"
                type="number"
                {...registerAccount("accountNumber")}
                required
              />
              {getErrorMessage(accountErrors, "accountNumber") && (
                <p className="text-destructive text-sm">
                  {getErrorMessage(accountErrors, "accountNumber")}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="bankCode"
                className="block text-sm font-medium text-default-700"
              >
                Bank
              </label>
              <Select
                onValueChange={(value) => {
                  setAccountValue("bankCode", value);
                  let bankName = banks.find(
                    (bank) => bank.code === value,
                  )?.name;
                  setAccountValue("bankName", bankName);
                }}
                defaultValue=""
                required
              >
                <SelectTrigger id="bankCode">
                  <SelectValue placeholder="Select a bank" />
                </SelectTrigger>
                <SelectContent>
                  {banks.map((bank) => (
                    <SelectItem key={bank.code} value={bank.code}>
                      {bank.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {getErrorMessage(accountErrors, "bankCode") && (
                <p className="text-destructive text-sm">
                  {getErrorMessage(accountErrors, "bankCode")}
                </p>
              )}
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsAddAccountDialogOpen(false)}
                type="button"
              >
                Cancel
              </Button>
              <Button variant="default" type="submit">
                Add Account
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorboltProfileComponent;
